import React, { useState } from 'react';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { Navigate, useLocation } from 'react-router-dom';

import ApplicationLoading from '../../../../common/ApplicationLoading';
import { Button } from '../../../../components/core';
import CopyButton from '../../../../components/core/CopyButton';
import { runtimeConfig } from '../../../../config/runtimeConfig';
import { useMerchant } from '../../../../contexts/MerchantContext';
import {
  useGetAgenticSettingsQuery,
  useGetCredentialsQuery,
  usePatchStoreReadinessMutation,
} from '../../../../store/modules/merchant/api';
import { agentHubPaths } from '../../../../util/constants';
import { toast } from '../../../../util/toast';
import { StatusChip } from '../../../ShopifyProvisioning/StatusChip';
import { buttonPaddingSx, getApiErrorMessage } from '../../../ShopifyProvisioning/utils';
import { ActionUrl, StepCard, WaitingHint } from '../components/ProvisioningStepParts';
import { useProvisioningPlatform } from '../useProvisioningPlatform';
import {
  buildSquarespaceConnectUrl,
  buildSquarespaceEllaSnippet,
  findSquarespaceCredential,
  getSquarespaceConnectionStatus,
  getSquarespaceProvisioningStatus,
  getSquarespaceSite,
  type SquarespaceConnectionStatus,
  type SquarespaceSite,
} from './squarespaceProvisioning';

const CONNECT_STEP_TITLE = 'Connect Skipify to Squarespace';
const SNIPPET_STEP_TITLE = 'Add the shopping agent to your site';

const Emphasis = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <Box component="span" sx={{ fontWeight: 700 }}>
    {children}
  </Box>
);

const SiteName = ({ site }: { site?: SquarespaceSite }): JSX.Element =>
  site ? <Emphasis>{site.host}</Emphasis> : <>your Squarespace site</>;

const ConnectStepContent = ({
  connection,
  connectUrl,
  site,
}: {
  connection: SquarespaceConnectionStatus;
  connectUrl?: string;
  site?: SquarespaceSite;
}): JSX.Element => {
  if (connection === 'connected') {
    return (
      <Typography
        variant="body2"
        sx={{ lineHeight: 1.5 }}
        data-testid="ella-provisioning-squarespace-connected"
      >
        Connected to <SiteName site={site} />.
      </Typography>
    );
  }

  if (!connectUrl) {
    return (
      <WaitingHint
        testId="ella-provisioning-squarespace-connect-url-placeholder"
        title="Your connect link isn't available"
      >
        Contact your Skipify representative to get it.
      </WaitingHint>
    );
  }

  return (
    <Stack spacing={1.25} data-testid="ella-provisioning-squarespace-connect-url">
      <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
        {connection === 'expired' ? (
          <>
            Squarespace connections expire after 7 days without activity. Open this link and approve
            access for <SiteName site={site} /> again to reconnect.
          </>
        ) : (
          <>
            Open this link and approve access for <SiteName site={site} />. Copy the snippet from
            step 2 first: Squarespace takes you straight to Code Injection once you connect.
          </>
        )}
      </Typography>
      <ActionUrl url={connectUrl} copyTitle="connect link" />
    </Stack>
  );
};

const SnippetBlock = ({ snippet }: { snippet: string }): JSX.Element => (
  <Box
    sx={{
      position: 'relative',
      border: 1,
      borderColor: 'divider',
      borderRadius: 1,
      bgcolor: 'action.hover',
    }}
  >
    <Box
      component="pre"
      data-testid="ella-provisioning-squarespace-snippet"
      sx={{
        m: 0,
        p: 2,
        pr: 6,
        overflowX: 'auto',
        fontFamily: 'monospace',
        fontSize: 13,
        lineHeight: 1.5,
      }}
    >
      {snippet}
    </Box>
    <Box sx={{ position: 'absolute', top: 4, right: 4 }}>
      <CopyButton copyString={snippet} title="snippet" />
    </Box>
  </Box>
);

const SnippetStepContent = ({
  snippet,
  site,
}: {
  snippet?: string;
  site?: SquarespaceSite;
}): JSX.Element => {
  if (!snippet) {
    return (
      <WaitingHint
        testId="ella-provisioning-squarespace-snippet-placeholder"
        title="Your shopping agent snippet isn't available"
      >
        Contact your Skipify representative for help.
      </WaitingHint>
    );
  }

  return (
    <Stack spacing={1.25}>
      <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
        Paste this snippet into the <Emphasis>Footer</Emphasis> field of Code Injection and click{' '}
        <Emphasis>Save</Emphasis>. Then tap Mark done.
      </Typography>
      <SnippetBlock snippet={snippet} />
      {site?.codeInjectionUrl ? (
        <ActionUrl url={site.codeInjectionUrl} copyTitle="Code Injection link" />
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          You can find Code Injection in your Squarespace site admin.
        </Typography>
      )}
    </Stack>
  );
};

export const ProvisioningSquarespacePage = (): JSX.Element => {
  const { search } = useLocation();
  const { merchantId } = useMerchant();
  const { platform, isLoading: isPlatformLoading } = useProvisioningPlatform();
  const [isRefreshingStatus, setIsRefreshingStatus] = useState(false);

  const {
    data: credentials,
    isLoading: areCredentialsLoading,
    refetch: refetchCredentials,
  } = useGetCredentialsQuery(
    { merchantId: merchantId ?? '', type: ['ecommerce'] },
    // The merchant connects in another tab.
    { skip: !merchantId, refetchOnFocus: true },
  );
  const {
    data: agenticSettings,
    isLoading: areAgenticSettingsLoading,
    refetch: refetchAgenticSettings,
  } = useGetAgenticSettingsQuery(merchantId, { skip: !merchantId });
  const [patchStoreReadiness, { isLoading: isMarkingDone }] = usePatchStoreReadinessMutation();

  if (isPlatformLoading || areCredentialsLoading || areAgenticSettingsLoading) {
    return <ApplicationLoading />;
  }

  if (platform !== 'squarespace') {
    return <Navigate to={`${agentHubPaths.DASHBOARD}${search}`} replace />;
  }

  const credential = findSquarespaceCredential(credentials);
  const connection = getSquarespaceConnectionStatus(credential);
  const site = getSquarespaceSite(credential);
  const connectUrl = buildSquarespaceConnectUrl(runtimeConfig.ecomConnectorApiBaseUrl);
  const { ellaApiUrl, ellaSdkUrl } = runtimeConfig;
  const snippet =
    merchantId && ellaApiUrl && ellaSdkUrl
      ? buildSquarespaceEllaSnippet({ merchantId, apiBase: ellaApiUrl, sdkUrl: ellaSdkUrl })
      : undefined;
  // Missing storeReadiness predates the gate, as in isAgenticStoreReady.
  const snippetDone = agenticSettings
    ? (agenticSettings.storeReadiness?.status ?? 'complete') === 'complete'
    : false;
  const provisioningStatus = getSquarespaceProvisioningStatus(connection, snippetDone);

  const checkStatus = async () => {
    setIsRefreshingStatus(true);
    try {
      await Promise.all([refetchCredentials().unwrap(), refetchAgenticSettings().unwrap()]);
      toast.success('Onboarding status refreshed.');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to refresh onboarding status.'));
    } finally {
      setIsRefreshingStatus(false);
    }
  };

  const markSnippetDone = async () => {
    try {
      await patchStoreReadiness({
        merchantId,
        body: { storeReadiness: { status: 'complete' } },
      }).unwrap();
      toast.success(`${SNIPPET_STEP_TITLE} marked done.`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to update this onboarding step.'));
    }
  };

  return (
    <Box data-testid="ella-provisioning-squarespace-page">
      <Stack spacing={3}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Status
            </Typography>
            <StatusChip label={provisioningStatus.label} color={provisioningStatus.color} />
          </Stack>
          <Button
            text="Check Status"
            sx={buttonPaddingSx}
            onClick={() => {
              void checkStatus();
            }}
            disabled={isRefreshingStatus}
            loading={isRefreshingStatus}
          />
        </Stack>

        {connection === 'expired' && (
          <Alert severity="warning" data-testid="ella-provisioning-squarespace-expired">
            Your Squarespace connection expired, so the shopping agent can&apos;t read your catalog.
            Reconnect in step 1 to restore it.
          </Alert>
        )}

        <Stack spacing={3}>
          <StepCard
            number={1}
            title={CONNECT_STEP_TITLE}
            status={
              connection === 'connected' ? 'done' : connection === 'expired' ? 'failed' : 'pending'
            }
            statusLabel={connection === 'expired' ? 'Expired' : undefined}
          >
            <ConnectStepContent connection={connection} connectUrl={connectUrl} site={site} />
          </StepCard>

          <StepCard number={2} title={SNIPPET_STEP_TITLE} status={snippetDone ? 'done' : 'pending'}>
            <SnippetStepContent snippet={snippet} site={site} />
            <Button
              text="Mark done"
              theme="secondary"
              variant="outlined"
              sx={buttonPaddingSx}
              onClick={() => {
                void markSnippetDone();
              }}
              disabled={connection !== 'connected' || snippetDone || isMarkingDone || !snippet}
              loading={isMarkingDone}
            />
          </StepCard>
        </Stack>

        <Typography variant="body2" color="text.secondary">
          Need help? Contact your Skipify representative if any step is blocked.
        </Typography>
      </Stack>
    </Box>
  );
};
